// AuthContext.js
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import supabase, {
  addToWaitlist as addToWaitlistAPI,
  resetPasswordForEmail,
  signIn,
  signInWithAzure,
  signInWithMagicLink,
  signOut,
  signUp,
  updatePassword,
  verifyBetaCode,
} from "../services/supabaseClient";

const logError = (location, error) => {
  console.error(`[AuthContext] Error in ${location}:`, {
    message: error.message,
    stack: error.stack,
  });
};

const AuthContext = createContext({
  user: null,
  loading: true,
  firmContext: null, // { firmId, roleName, permissions }
  hasPermission: (permission) => false,
  canAccessCase: (caseId) => false,
  userFirms: [],
  switchFirm: (firmId) => {},
  isSessionValid: () => false,
  refreshSession: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firmContext, setFirmContext] = useState(null);
  const [session, setSession] = useState(null);
  const [firmRoleCache, setFirmRoleCache] = useState(new Map());
  const [userFirms, setUserFirms] = useState([]);
  const [sessionStatus, setSessionStatus] = useState("valid"); // 'valid' | 'refreshing' | 'expired'

  const getFirmRoleData = useCallback(
    async (firmId, forceRefresh = false) => {
      // Check cache first unless force refresh
      if (!forceRefresh && firmRoleCache.has(firmId)) {
        return firmRoleCache.get(firmId);
      }

      try {
        const { data, error } = await supabase
          .from("firm_users")
          .select(
            `
          firm_id,
          roles (
            role_name,
            permissions
          ),
          firms (
            name,
            settings
          )
        `
          )
          .eq("firm_id", firmId)
          .eq("user_id", user.id)
          .single();

        if (error) throw error;

        // Ensure settings has default values
        const settings = {
          enable_qa: false,  // Default value
          ...data.firms.settings  // Spread existing settings
        };

        const firmRoleData = {
          firmId: data.firm_id,
          roleName: data.roles.role_name,
          permissions: data.roles.permissions,
          firmName: data.firms.name,
          firmSettings: settings,
          lastUpdated: new Date().getTime(),
        };

        // Update cache
        setFirmRoleCache((prev) => new Map(prev).set(firmId, firmRoleData));

        return firmRoleData;
      } catch (error) {
        console.error("Error fetching firm role data:", error);
        return null;
      }
    },
    [firmRoleCache, user?.id]
  );

  // Add a helper function to check QA setting
  const isQAEnabled = useCallback(() => {
    return firmContext?.firmSettings?.enable_qa || false;
  }, [firmContext]);

  // Fetch firm context whenever user changes
  useEffect(() => {
    const fetchFirmContext = async () => {
      try {
        if (!user) {
          console.log("[AuthContext] No user, skipping firm context fetch");
          setFirmContext(null);
          return;
        }

        console.log("[AuthContext] Fetching firm context for user:", user.id);
        const { data, error } = await supabase
          .from("firm_users")
          .select("firm_id")
          .eq("user_id", user.id)
          .single();

        if (error) {
          console.error("[AuthContext] Error fetching firm context:", error);
          return;
        }

        try {
          const firmRoleData = await getFirmRoleData(data.firm_id);
          if (firmRoleData) {
            console.log("[AuthContext] Setting firm context");
            setFirmContext(firmRoleData);
          } else {
            console.error("[AuthContext] No firm role data returned");
          }
        } catch (error) {
          console.error("[AuthContext] Error getting firm role data:", error);
        }
      } catch (error) {
        console.error(
          "[AuthContext] Unexpected error in firm context fetch:",
          error
        );
      }
    };

    fetchFirmContext().catch((error) => {
      console.error(
        "[AuthContext] Unhandled error in firm context fetch:",
        error
      );
    });
  }, [user]);

  // Helper to check permissions
  const hasPermission = useCallback(
    (category, action) => {
      if (!firmContext?.permissions) return false;

      // Super admins can do everything
      if (firmContext.permissions.super_admin) return true;

      // Handle both formats: hasPermission('super_admin') and hasPermission('case_actions', 'update_status')
      if (action === undefined) {
        // Single string format - check if it's a direct boolean flag
        return !!firmContext.permissions[category];
      }

      // Category/action pair format - check nested permissions
      return !!firmContext.permissions[category]?.[action];
    },
    [firmContext]
  );

  // Helper to check case access
  const canAccessCase = useCallback(
    async (caseId) => {
      if (!user || !firmContext) return false;

      // Super admins can access everything
      if (firmContext.permissions.super_admin) return true;

      const { data: caseData } = await supabase
        .from("cases")
        .select("firm_id, user_id")
        .eq("case_id", caseId)
        .single();

      if (!caseData) return false;

      const caseAccess = firmContext.permissions?.case_access || {};

      // Check access levels in order of scope (broadest to narrowest)
      if (
        caseAccess.all_within_firm &&
        caseData.firm_id === firmContext.firmId
      ) {
        return true;
      }

      if (caseAccess.own && caseData.user_id === user.id) {
        return true;
      }

      if (caseAccess.assigned) {
        const { data: analyst } = await supabase
          .from("case_analysts")
          .select("user_id")
          .eq("case_id", caseId)
          .eq("user_id", user.id)
          .single();

        if (analyst) return true;
      }

      return false;
    },
    [user, firmContext]
  );

  // Add new function to check if session needs refresh
  const needsRefresh = useCallback(() => {
    if (!session?.expires_at) return true;

    // Refresh if less than 25% of token lifetime remains
    const expiresAt = session.expires_at * 1000; // convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    const tokenLifetime = 3600 * 1000; // 1 hour in milliseconds

    return timeUntilExpiry < tokenLifetime * 0.25;
  }, [session]);

  // Enhanced refresh token function
  const refreshSession = useCallback(
    async (forceRefresh = false) => {
      try {
        console.log("[AuthContext] Checking session refresh");
        // Always check expiration
        const now = Math.floor(Date.now() / 1000); // Current time in seconds
        if (
          !forceRefresh &&
          session?.access_token &&
          session.expires_at > now
        ) {
          return session;
        }

        console.log("[AuthContext] Refreshing session");
        const {
          data: { session: newSession },
          error,
        } = await supabase.auth.refreshSession();

        if (error) {
          console.error("[AuthContext] Session refresh error:", error);
          setSessionStatus("expired");
          return null;
        }

        console.log("[AuthContext] Session refreshed successfully");
        setSession(newSession);
        return newSession;
      } catch (error) {
        console.error(
          "[AuthContext] Unexpected error refreshing session:",
          error
        );
        setSessionStatus("error");
        return null;
      }
    },
    [session]
  );

  // Add periodic refresh check
  useEffect(() => {
    if (!session) return;

    const checkInterval = setInterval(() => {
      if (needsRefresh()) {
        refreshSession();
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkInterval);
  }, [session, needsRefresh, refreshSession]);

  // Handle initial session check
  useEffect(() => {
    const initializeAuth = async () => {
      let error = null; // Define error variable at function scope
      try {
        const {
          data: { session: initialSession },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          error = sessionError; // Store the error
          logError("initializeAuth - getSession", error);
          throw error;
        }

        if (initialSession) {
          setSession(initialSession);
          setUser(initialSession.user);
          if (needsRefresh()) {
            await refreshSession(true);
          }
        }
      } catch (err) {
        error = err; // Store any caught error
        logError("initializeAuth", err);
        setUser(null);
        setSession(null);
        return;
      } finally {
        if (!error) {
          // Now error is properly in scope
          setLoading(false);
        }
      }
    };

    initializeAuth().catch((error) => {
      logError("initializeAuth - uncaught", error);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const fetchUserFirms = async () => {
      try {
        if (!user) {
          console.log("[AuthContext] No user, clearing firms");
          setUserFirms([]);
          return;
        }

        console.log("[AuthContext] Fetching firms for user:", user.id);
        const { data, error } = await supabase
          .from("firm_users")
          .select(
            `
            firm_id,
            roles (role_name),
            firms (name)
          `
          )
          .eq("user_id", user.id);

        if (error) {
          console.error("[AuthContext] Error fetching user firms:", error);
          return;
        }

        console.log("[AuthContext] Processing firms data:", data);
        const firms = data.map((fu) => ({
          firmId: fu.firm_id,
          roleName: fu.roles.role_name,
          firmName: fu.firms.name,
        }));

        setUserFirms(firms);
      } catch (error) {
        console.error("[AuthContext] Unexpected error fetching firms:", error);
      }
    };

    fetchUserFirms().catch((error) => {
      console.error("[AuthContext] Unhandled error in firms fetch:", error);
    });
  }, [user]);

  const switchFirm = async (firmId) => {
    if (!user) return;

    const firmRoleData = await getFirmRoleData(firmId, true); // Force refresh
    if (firmRoleData) {
      setFirmContext(firmRoleData);
      // You might want to trigger a refresh of relevant data here
    }
  };

  const value = {
    user,
    setUser,
    session,
    setSession,
    refreshSession,
    getAccessToken: async () => {
      const currentSession = await refreshSession();
      return currentSession
        ? {
            access_token: currentSession.access_token,
            refresh_token: currentSession.refresh_token,
          }
        : null;
    },
    resetPasswordForEmail: async (email) => {
      return resetPasswordForEmail(email);
    },
    signIn: async (credentials) => {
      try {
        const { email, password } = credentials;

        const { user, session, error } = await signIn(email, password);

        if (error) {
          console.error("[AuthContext] Sign in error:", error);
          return { user: null, error };
        }

        if (user && session) {
          try {
            setUser(user);
            setSession(session);
            await supabase.auth.setSession(session);
            return { user, error: null };
          } catch (error) {
            console.error("[AuthContext] Error updating state:", error);
            return { user: null, error };
          }
        }

        console.error("[AuthContext] Sign in failed: No user or session");
        return {
          user: null,
          error: new Error("Sign in failed: No user or session returned"),
        };
      } catch (error) {
        console.error("[AuthContext] Unexpected error during sign in:", error);
        return { user: null, error };
      }
    },
    signUp: async (credentials) => {
      const { user, session, needsEmailConfirmation, error } = await signUp(
        credentials.email,
        credentials.password
      );

      if (error) {
        return { user: null, error };
      }

      if (needsEmailConfirmation) {
        return { user: null, error: null, needsEmailConfirmation: true };
      }

      if (user && session) {
        setUser(user);
        setSession(session);
        return { user, error: null, needsEmailConfirmation: false };
      }

      return {
        user: null,
        error: new Error("Sign up failed: Unexpected response format"),
      };
    },
    signOut: async () => {
      const { error } = await signOut();
      if (!error) {
        setUser(null);
        setSession(null);
      }
      return { error };
    },
    signInWithMagicLink: async (email) => {
      return await signInWithMagicLink(email);
    },
    signInWithAzure: async () => {
      const { data, error } = await signInWithAzure();
      if (data.user) {
        setUser(data.user);
        setSession(data.session);
      }
      return { data, error };
    },
    updatePassword: async (password) => {
      return updatePassword(password);
    },
    verifyBetaCode: async (code) => {
      return await verifyBetaCode(code);
    },
    addToWaitlist: async (email) => {
      return await addToWaitlistAPI(email);
    },
    firmContext,
    hasPermission,
    canAccessCase,
    getFirmRoleData,
    firmRoleCache,
    clearFirmRoleCache: () => setFirmRoleCache(new Map()),
    userFirms,
    switchFirm,
    sessionStatus,
    isSessionValid: useCallback(
      () => sessionStatus === "valid",
      [sessionStatus]
    ),
    isQAEnabled,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
